import { useState } from "react";
import { useSEO } from "@/hooks/use-seo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CheckCircle, ChevronDown, ChevronUp, Shield, AlertTriangle, TrendingUp, Target, Brain, BarChart2, Calendar } from "lucide-react";
import { Link } from "wouter";

const FAQS = [
  {
    q: "Jaká je nejlepší strategie pro začátečníky v pokeru?",
    a: "Začněte tight-aggressive stylem (TAG) — hrajte méně kombinací, ale tyto kombinace hrajte agresivně. Soustřeďte se na silné startovní handy a vyhněte se hraní ze slabých pozic.",
  },
  {
    q: "Jak často bych měl bluffovat?",
    a: "Bluffujte jen tehdy, když vaše sázení vypráví konzistentní příběh a soupeři mají důvod své karty složit. Nadměrné blafování je jednou z nejčastějších chyb začátečníků.",
  },
  {
    q: "Mám hrát raději turnaje, nebo cash games?",
    a: "Turnaje nabízejí větší varianci a šanci na velké výhry, cash games jsou stabilnější a předvídatelnější. Pro začátečníky je obecně snazší učit se v cash games.",
  },
  {
    q: "Jak poznám, že soupeř bluffuje?",
    a: "Sledujte vzorce sázek a chování. V online pokeru velikosti sázek a časové prodlevy, v live pokeru i řeč těla. Klíčem je konzistentní pozorování po celou dobu hry.",
  },
];

const MISTAKES = [
  {
    mistake: "Hraní příliš mnoha kombinací",
    fix: "Naučte se disciplíně a hrajte jen silné handy z raných pozic. Tight-aggressive styl je základem úspěchu.",
  },
  {
    mistake: "Ignorování pozice",
    fix: "Pamatujte: na buttonu můžete hrát širší range, v early pozici buďte opatrní. Pozice je jedna z největších výhod v pokeru.",
  },
  {
    mistake: "Špatný bankroll management",
    fix: "Používejte pravidlo max. 5–10 % bankrollu na jednu hru. Nikdy nechoďte all-in se svým celkovým bankrollem.",
  },
  {
    mistake: "Nadměrné bluffování",
    fix: "Blafujte jen, když váš příběh dává smysl a soupeři mohou složit. Bluff bez logiky je vyhazování peněz.",
  },
  {
    mistake: "Podceňování soupeřů",
    fix: "Sledujte jejich vzorce sázek a přizpůsobte se. Každý hráč má jiný styl — to je vaše výhoda, pokud pozorujete.",
  },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#eef1f7] last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-4 text-left gap-4">
        <span className="font-semibold text-gray-900 text-sm">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-[#003da5] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && <p className="text-gray-600 text-sm leading-relaxed pb-4">{a}</p>}
    </div>
  );
}

export default function PokerStrategiePage() {
  useSEO({ title: "Pokrová Strategie 2026 – Jak vyhrávat více u stolu | HrajBonus", description: "Ověřené pokrové strategie pro začátečníky i pokročilé. Pozice u stolu, blufování, bankroll management a čtení soupeřů. Zlepšete hru. 18+",
  url: "https://hrajbonus.com/poker/strategie", });

  return (
    <div className="min-h-screen bg-[#f5f7fb]" style={{ fontFamily: "Poppins, sans-serif" }}>
      <Header />

      {/* Hero */}
      <section className="relative pt-16 pb-0 overflow-hidden">
        <div className="absolute inset-0 z-0" style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }} />
        <div className="absolute inset-0 z-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-14 sm:py-16">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2 text-white/60 text-sm font-semibold">
              <Link href="/poker" className="hover:text-white transition-colors">Poker</Link>
              <span>/</span>
              <span className="text-white/90">Strategie</span>
            </div>
            <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-white/70 text-xs font-semibold" data-testid="badge-aktualizovano-strategie">
              <Calendar className="w-3 h-3" /> Aktualizováno: 12. března 2026
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-5">
            Pokerové strategie – tipy pro začátečníky i pokročilé
          </h1>
          <p className="text-white/75 text-lg leading-relaxed mb-5">
            Poker není jen hra štěstí, ale především hra strategie, matematiky a psychologie. Rozdíl mezi rekreačním hráčem a profesionálem spočívá právě v tom, jak umí přemýšlet o svých kombinacích, soupeřích a situacích u stolu.
          </p>
          <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 w-fit text-white/70 text-xs font-semibold">
            <Shield className="w-3.5 h-3.5 text-white/50" /> Pouze pro hráče 18+. Hrajte zodpovědně.
          </div>
        </div>
        <div className="relative z-10">
          <svg viewBox="0 0 1440 44" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none" style={{ height: "44px" }}>
            <rect x="0" y="40" width="1440" height="4" fill="#f5f7fb" />
            <path d="M0 44L1440 44L1440 0C1200 35 720 40 0 0L0 44Z" fill="#f5f7fb" />
          </svg>
        </div>
      </section>

      {/* Poker cross-navigation */}
      <nav className="bg-[#f5f7fb] border-b border-[#dde4ee] sticky top-16 z-20" data-testid="poker-nav">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="flex items-center gap-1 overflow-x-auto py-3 -mx-1">
            {[
              { href: "/poker", label: "Poker přehled", active: false },
              { href: "/poker/kombinace", label: "Kombinace", active: false },
              { href: "/poker/strategie", label: "Strategie", active: true },
              { href: "/poker/jak-hrat", label: "Jak hrát", active: false },
            ].map(({ href, label, active }) => (
              <Link key={href} href={href}>
                <div
                  className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                    active
                      ? "bg-[#003da5] text-white"
                      : "text-gray-600 hover:bg-white hover:text-[#003da5]"
                  }`}
                  data-testid={`poker-nav-${href.replace(/\//g, "-")}`}
                >
                  {label}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </nav>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-10 space-y-8">

        {/* Why strategy matters */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Proč je strategie v pokeru důležitá</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Úspěch v pokeru se dlouhodobě neodvíjí od náhody. Každé rozhodnutí — zda dorovnat, složit nebo navýšit — ovlivňuje vaši očekávanou hodnotu (EV). Strategie vám umožní minimalizovat ztráty a maximalizovat výhry v dlouhodobém horizontu.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { icon: TrendingUp, title: "Očekávaná hodnota (EV)", desc: "Každé rozhodnutí má matematickou hodnotu. Konzistentně správná rozhodnutí vedou k zisku v dlouhodobém horizontu, i když krátkodobě prohráváte." },
                { icon: Brain, title: "Psychologie a reads", desc: "Porozumění soupeřům, jejich vzorcům sázek a tell signálům vám dává informační výhodu, která přesahuje sílu karet." },
                { icon: Target, title: "Disciplína a bankroll", desc: "I nejlepší strategie selže bez správného bankroll managementu. Disciplína je základ, na kterém stojí vše ostatní." },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="bg-[#f3f6fb] rounded-xl p-4">
                  <Icon className="w-6 h-6 text-[#003da5] mb-3" />
                  <div className="font-bold text-gray-900 text-sm mb-2">{title}</div>
                  <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Basic strategies */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-6">Základní pokerové strategie pro začátečníky</h2>

            <div className="space-y-6">
              <div className="border border-[#003da5]/20 rounded-xl p-5 bg-[#f0f5ff]">
                <h3 className="font-bold text-[#003da5] text-lg mb-3">Tight-aggressive styl (TAG)</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Nejčastěji doporučovaná strategie pro začátečníky. Hrajete méně startovních kombinací, ale když už vstoupíte do hry, hrajete agresivně — navyšujete a tlačíte na soupeře. TAG styl minimalizuje chyby a zároveň vám dává iniciativu u stolu.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 text-base mb-4">Výběr startovních kombinací</h3>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="font-bold text-green-800 text-sm mb-2">Silné handy — vždy hrajte</div>
                    <p className="text-green-700 text-sm">AA, KK, QQ, JJ, AK suited, AQ suited</p>
                    <p className="text-green-600 text-xs mt-2">Hrajte agresivně z jakékoli pozice.</p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                    <div className="font-bold text-yellow-800 text-sm mb-2">Střední handy — záleží na pozici</div>
                    <p className="text-yellow-700 text-sm">TT, 99, AJ, KQ, středně suited connectors</p>
                    <p className="text-yellow-600 text-xs mt-2">Silnější z pozdní pozice, opatrněji z rané.</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="font-bold text-red-800 text-sm mb-2">Slabé handy — nejčastěji složte</div>
                    <p className="text-red-700 text-sm">7-2, 9-3, slabé off-suit kombinace</p>
                    <p className="text-red-600 text-xs mt-2">Disciplína znamená umět zahodit.</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#f3f6fb] rounded-xl p-5">
                <h3 className="font-bold text-gray-900 text-base mb-3">Pozice u stolu</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Čím později hrajete (např. na buttonu), tím více informací o soupeřích máte. To je obrovská výhoda — můžete hrát širší rozsah kombinací a lépe vyhodnocovat situaci.
                </p>
                <div className="grid sm:grid-cols-3 gap-3 text-sm">
                  <div className="text-center p-3 bg-white rounded-lg border border-[#dde4ee]">
                    <div className="font-bold text-red-600 mb-1">Early position</div>
                    <div className="text-gray-500 text-xs">Hrajte jen nejsilnější handy. Soupeři za vámi mají informační výhodu.</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border border-[#dde4ee]">
                    <div className="font-bold text-yellow-600 mb-1">Middle position</div>
                    <div className="text-gray-500 text-xs">Mírně širší range. Záleží na aktivitě hráčů před vámi.</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border border-[#dde4ee]">
                    <div className="font-bold text-green-600 mb-1">Late position (Button)</div>
                    <div className="text-gray-500 text-xs">Nejsilnější pozice. Hrajte nejšíreji, máte nejvíce informací.</div>
                  </div>
                </div>
              </div>

              <div className="bg-[#f3f6fb] rounded-xl p-5">
                <h3 className="font-bold text-gray-900 text-base mb-3">Bankroll management</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Nikdy neriskujte více, než si můžete dovolit prohrát. Správný bankroll management vás ochrání před bankrotem i v délkách špatné série.
                </p>
                <div className="flex items-start gap-3 bg-white border border-[#dde4ee] rounded-lg p-3">
                  <CheckCircle className="w-4 h-4 text-[#003da5] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">
                    <span className="font-bold">Obecné pravidlo: </span>
                    do jedné hry nebo turnaje nevstupujte s více než 5–10 % svého bankrollu. Pro cash games mějte na stole alespoň 20–30 buy-inů.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Advanced strategies */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-6">Pokročilé pokerové strategie</h2>

            <div className="space-y-5">
              <div className="border-l-4 border-[#003da5] pl-5">
                <h3 className="font-bold text-gray-900 text-base mb-2">Bluffování</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Bluffování je nedílnou součástí pokeru, ale musí být promyšlené. Blafujte proti hráčům, kteří dokážou zahodit karty, a v situacích, kdy příběh vaší hry dává smysl. Bluff bez logiky soupeřovi nic neřekne — silný bluff vypráví konzistentní příběh od preflopu po river.
                </p>
              </div>

              <div className="border-l-4 border-amber-400 pl-5">
                <h3 className="font-bold text-gray-900 text-base mb-2">Semi-bluff</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Sázíte s kombinací, která zatím není silná, ale má potenciál se zlepšit (např. flush draw nebo otevřená postupka). I když vás soupeř dorovná, stále máte outs — šance vyhrát na turnu nebo riveru. Semi-bluff je jednou z nejvýkonnějších technik v pokeru.
                </p>
              </div>

              <div className="border-l-4 border-green-500 pl-5">
                <h3 className="font-bold text-gray-900 text-base mb-2">Čtení soupeřů</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Pozorujte vzorce sázek, rychlost reakcí a chování u stolu. Online sledujte časy rozhodnutí a velikosti sázek. Live poker umožňuje vnímat i neverbální signály (tzv. tells).
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="bg-[#f3f6fb] rounded-lg p-3">
                    <div className="font-bold text-gray-800 text-xs mb-1.5">Online poker</div>
                    <ul className="space-y-1">
                      {["Rychlost rozhodnutí (tank = silná ruka nebo bluff)", "Velikost sázky (overbety = polarizovaný range)", "Frekvence c-betu a fold to 3bet"].map(t => (
                        <li key={t} className="text-gray-600 text-xs flex items-start gap-1.5"><span className="text-[#003da5] mt-0.5">•</span>{t}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-[#f3f6fb] rounded-lg p-3">
                    <div className="font-bold text-gray-800 text-xs mb-1.5">Live poker</div>
                    <ul className="space-y-1">
                      {["Řeč těla a mimika při pohledu na karty", "Jak drží žetony při sázení", "Změny v dýchání nebo chování při silné ruce"].map(t => (
                        <li key={t} className="text-gray-600 text-xs flex items-start gap-1.5"><span className="text-[#003da5] mt-0.5">•</span>{t}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="border-l-4 border-purple-500 pl-5">
                <h3 className="font-bold text-gray-900 text-base mb-2">Range thinking</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Nepřemýšlejte jen nad jednou možnou kombinací soupeře. Uvažujte o celém spektru karet, které může mít podle svých akcí. Silný hráč neřekne soupeř má AA, ale spíše soupeřův range obsahuje 20 % velmi silných handů, 50 % středních a 30 % bluffů. Toto myšlení vede k fundamentálně lepším rozhodnutím.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Tournament vs Cash Game */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-[#eef1f7]">
              <h2 className="text-2xl font-black text-gray-900">Turnajová vs. cash game strategie</h2>
              <p className="text-gray-500 text-sm mt-1">Pořadí kombinací je v obou formátech stejné — přístup se ale zásadně liší.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#f3f6fb]">
                    <th className="text-left px-5 py-3 font-bold text-gray-700 text-xs uppercase tracking-wide">Faktor</th>
                    <th className="text-left px-5 py-3 font-bold text-[#003da5] text-xs uppercase tracking-wide">Turnaje</th>
                    <th className="text-left px-5 py-3 font-bold text-gray-700 text-xs uppercase tracking-wide">Cash games</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Hodnota žetonů", "Nelineární (ICM model)", "Vždy stejná"],
                    ["Variance", "Vysoká", "Nižší"],
                    ["Cíle", "Přežití a postup", "Maximalizace EV"],
                    ["Styl hry", "Agrese v pozdějších fázích", "Stabilní a konzistentní přístup"],
                    ["Bankroll nároky", "Nižší buy-in, potenciálně velká výhra", "Stabilní, předvídatelné výsledky"],
                    ["Vhodné pro", "Dobrodružné hráče, kteří chtějí velký pot", "Hráče preferující stabilitu a trénink"],
                  ].map(([factor, tournament, cash], i) => (
                    <tr key={factor} className={`border-t border-[#eef1f7] ${i % 2 === 1 ? "bg-[#fafbfe]" : ""}`}>
                      <td className="px-5 py-3 font-semibold text-gray-900 text-sm">{factor}</td>
                      <td className="px-5 py-3 text-[#003da5] font-medium text-sm">{tournament}</td>
                      <td className="px-5 py-3 text-gray-600 text-sm">{cash}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Common mistakes */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-5">Časté chyby hráčů pokeru</h2>
            <div className="space-y-4">
              {MISTAKES.map(({ mistake, fix }) => (
                <div key={mistake} className="flex items-start gap-4 p-4 rounded-xl border border-[#eef1f7] bg-[#fafbfe]">
                  <div className="flex-shrink-0 mt-0.5">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm mb-1">{mistake}</div>
                    <div className="flex items-start gap-1.5 text-gray-600 text-sm">
                      <CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                      {fix}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EV quick reference */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Rychlý přehled: matematika a EV v pokeru</h2>
            <p className="text-gray-600 leading-relaxed mb-5">
              Znalost základní matematiky vám pomůže dělat správná rozhodnutí i pod tlakem. Nemusíte být matematik — stačí znát klíčové pojmy a jak je aplikovat.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: "Pot odds", desc: "Poměr velikosti potu k velikosti cally. Pokud máte 33 % pot odds a vaše šance vyhrát je vyšší, call je ziskový." },
                { title: "Outs", desc: "Počet karet, které zlepší vaši ruku. S flush drawem máte 9 outů, s otevřenou postupkou 8 outů." },
                { title: "Rule of 2 and 4", desc: "Vynásobte počet outů 4 (na flopu se dvěma kartami k vidění) nebo 2 (na turnu s jednou kartou) pro přibližnou šanci dokončení." },
                { title: "Pozitivní EV (+EV)", desc: "Rozhodnutí s kladnou očekávanou hodnotou — vydělává v dlouhodobém horizontu, i když krátkodobě prohraná." },
              ].map(({ title, desc }) => (
                <div key={title} className="bg-[#f3f6fb] rounded-xl p-4">
                  <div className="font-bold text-gray-900 text-sm mb-2">{title}</div>
                  <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Responsible gaming */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Odpovědné hraní v pokeru</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Strategie je klíčem k úspěchu, ale poker zůstává hrou o peníze. Nastavte si limity a dodržujte bankroll management. Pokud máte pocit, že nad hraním ztrácíte kontrolu, využijte sebeomezující opatření dostupná v online kasinech.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-5">
              {[
                "Nastavte si pevné denní, týdenní nebo měsíční limity",
                "Nikdy nehrájte, abyste vyhrávali zpátky prohrané peníze",
                "Dělejte pravidelné přestávky — unavený hráč dělá drahé chyby",
              ].map(item => (
                <div key={item} className="flex items-start gap-2.5 text-gray-700 text-sm">
                  <Shield className="w-4 h-4 text-[#003da5] mt-0.5 flex-shrink-0" />
                  {item}
                </div>
              ))}
              <div className="flex items-start gap-2.5 text-gray-700 text-sm">
                <Shield className="w-4 h-4 text-[#003da5] mt-0.5 flex-shrink-0" />
                Pokud ztrácíte kontrolu, obraťte se na{" "}
                <a href="https://www.gamcare.org.uk/" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline font-semibold" data-testid="link-gamcare-poker-strategie">GamCare</a>{" "}
                nebo podobnou organizaci
              </div>
            </div>
            <div className="flex items-start gap-3 bg-[#f0f5ff] border border-[#003da5]/15 rounded-xl p-4">
              <BarChart2 className="w-5 h-5 text-[#003da5] mt-0.5 flex-shrink-0" />
              <p className="text-gray-700 text-sm leading-relaxed">
                <span className="font-bold text-[#003da5]">Pouze pro hráče 18+. </span>
                Hrajte zodpovědně. Poker je zábava — a nejlepší strategie začíná tím, že máte jasno ve svých limitech.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-5">Časté dotazy</h2>
            <div>
              {FAQS.map(item => <FaqItem key={item.q} q={item.q} a={item.a} />)}
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <div className="flex items-center justify-center gap-2 text-xs text-gray-400 font-medium pb-4">
          <Shield className="w-3.5 h-3.5" />
          <span>Pouze pro hráče 18+. Hrajte zodpovědně. Podmínky se vztahují.</span>
        </div>

      </div>
      <Footer />
    </div>
  );
}
